import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_EDITOR,
  KEY_ENTER_COMMAND,
  COPY_COMMAND,
  CUT_COMMAND,
  DELETE_CHARACTER_COMMAND,
  DELETE_LINE_COMMAND,
  DELETE_WORD_COMMAND,
  ElementNode,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_DOWN_COMMAND,
  LexicalNode,
  RangeSelection,
  TextNode,
} from 'lexical';
import { $createInsertNode, PhxInsertNode } from '../../nodes/PhxInsertNode';
import { $createDeleteNode, $isDeleteNode, PhxDeleteNode } from '../../nodes/PhxDeleteNode';
import { useEffect } from 'react';
import { useTrackChanges } from '../../context/SharedTrackChangesContext';
import { $isPhxParagraphNode } from '../../nodes/PhxParagraphNode';
import { mergeRegister } from '@lexical/utils';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  $isListNode,
  PhxListNode,
  PhxListItemNode,
} from '../../nodes/PhxListNode';
import { $getAllListItems } from '../../nodes/PhxListNode/utils';

/**
 * Merge all delete nodes that are next to each other
 *
 * @param {RangeSelection} selection - Current selection
 *
 */
function mergeDelete(selection: RangeSelection, username: string) {
  let selectedNodes = selection.getNodes();
  // merge all the delete nodes that are next to each other within the nodes that are currently selected
  for (let currentNode of selectedNodes) {
    if (currentNode.__type == 'paragraph') {
      continue;
    }

    let mainNode = currentNode.getTopLevelElement();
    let childNodes = (mainNode as any)?.getChildren() ?? [];
    let prevNode = null;
    let mergedDeleteNodes = [];
    let mergedNode = null;
    for (let node of childNodes) {
      if (!node || typeof node !== 'object') continue;
      if (node.__type === 'delete' && prevNode == null) {
        prevNode = node;
        if (mergedNode) {
          mergedDeleteNodes.push(mergedNode);
          mergedNode = null;
        }
        continue;
      }
      if (node.__type == 'delete' && prevNode !== null) {
        let prevChildNodes = (prevNode as any).getChildren();
        let currentChildNodes = (node as any).getChildren();
        let newChildNodes = [...prevChildNodes, ...currentChildNodes];
        let newDeleteNode = $createDeleteNode(username);
        newDeleteNode.append(...newChildNodes);
        node.insertBefore(newDeleteNode);
        node.remove();
        prevNode.remove();
        prevNode = newDeleteNode;
        mergedNode = newDeleteNode;
      } else {
        prevNode = null;
        if (mergedNode) {
          mergedDeleteNodes.push(mergedNode);
          mergedNode = null;
        }
      }
    }
  }
}

/**
 * Handles the selection when multiple nodes are selected.
 *
 * @param {LexicalNode} node - The node that was selected.
 * @param {number} offset - anchor offset / focus offset value.
 * @param {string} selectedArea - Left or Right half that was selected.
 *
 */
function handleMultiNodeSplit(node: any, offset: number, selectedArea: string, username: string) {
  const parentNode = node.getParent();
  if (parentNode && parentNode.__type === 'insert') {
    if (offset === -1) {
      // when the full node is selected
      node.remove();
      return;
    }
    let parentInsertNode: any = node.getParent();
    let split_nodes: [Text, Text] | any = null;

    let newParaNode = $createParagraphNode();
    newParaNode.append(node);
    split_nodes = node.splitText(...[offset]);
    // depending on which half was selected split the node accordingly
    if (selectedArea == 'left') {
      if (offset === 0) {
        parentInsertNode.append(node);
        //   $setSelection(node.select(0, 0));
        return [node];
      } else if (node.__text.length === offset) {
        split_nodes[0].remove();
        let newTextNode = $createTextNode('');
        parentInsertNode.append(newTextNode);
        return newTextNode;
      } else {
        split_nodes[0].remove();
        parentInsertNode.append(split_nodes[1]);
        //   $setSelection(split_nodes[1].select(0, 0));
        return [split_nodes[1]]; // if a node is returned within an area it means that this particular node has to be selected and the pointer must be kept at position (0, 0) to handle edge cases
      }
    } else {
      if (offset === 0) {
        split_nodes[0].remove();
        let newTextNode = $createTextNode('');
        parentInsertNode.append(newTextNode);
        return newTextNode;
      } else if (node.__text.length === offset) {
        parentInsertNode.append(node);
        return node;
      } else {
        split_nodes[1].remove();
        parentInsertNode.append(split_nodes[0]);
        return split_nodes[0];
      }
    }
  }
  // splitting delete nodes
  if (node.getParent().__type === 'delete') {
    if (offset === -1) {
      // when the full node is selected
      return;
    }
    if (selectedArea === 'right') {
      return node;
    } else {
      // $setSelection(node.select(0,0));
      return [node];
    }
  }

  // case when the selected node is a text node of a paragraph node (pre-existing text case)
  let delNode = $createDeleteNode(username);
  node.insertBefore(delNode);

  if (offset === -1) {
    delNode.append(node);
    return;
  }

  let split_nodes = null;
  let newParaNode = $createParagraphNode();
  newParaNode.append(node);
  split_nodes = node.splitText(...[offset]);
  if (selectedArea == 'left') {
    // if the cursor has selected the left half
    if (offset == 0) {
      delNode.insertBefore(split_nodes[0]);
      delNode.remove();
    } else if (node.__text.length === offset) {
      delNode.append(split_nodes[0]);
    } else {
      delNode.append(split_nodes[0]);
      delNode.insertAfter(split_nodes[1]);
    }
  } else {
    // if the cursor has selected the right half
    if (offset == 0) {
      delNode.append(split_nodes[0]);
    } else if (node.__text.length === offset) {
      delNode.insertAfter(split_nodes[0]);
      delNode.remove();
    } else {
      delNode.append(split_nodes[1]);
      delNode.insertBefore(split_nodes[0]);
    }
  }

  return split_nodes[0];
}

/**
 * Handles the selection within a single node.
 *
 * @param {LexicalNode} node - The node that was selected.
 * @param {number} anchorOffset - anchor offset value
 * @param {number} focusOffset - focus offset value
 * @param {string | null} data - The data to be inserted
 */
function handleSingleNodeSplit(
  node: any,
  anchorOffset: number,
  focusOffset: number,
  data: string | null,
  username: string
) {
  let selectedArea = 'left';
  const parentNode = node.getParent();
  // logic for deciding which half was selected within the node
  if (anchorOffset < focusOffset) {
    selectedArea = 'right';
  }

  if (anchorOffset == node?.getTextContent().length) {
    selectedArea = 'right';
  } else if (anchorOffset == 0) {
    selectedArea = 'left';
  }
  // splitting the different types of nodes accordingly
  if (parentNode && parentNode.__type == 'delete') {
    node.select();
  }
  // all insert based nodes that were selected have to be removed
  else if (parentNode && parentNode.__type == 'insert') {
    let parentInsertNode: LexicalNode | any = node.getParent();
    let newParaNode = $createParagraphNode();
    newParaNode.append(node);
    if (node.__type === 'image') {
      node.remove();
      return true;
    }
    let splitNodes = node.splitText(...[anchorOffset, focusOffset]);
    if (splitNodes.length == 1) {
      let parentParaNode = parentInsertNode.getParent();
      parentInsertNode.remove();
      parentParaNode.select();
    } else if (splitNodes.length == 2) {
      if (selectedArea == 'left') {
        splitNodes[0].remove();
        parentInsertNode.append(splitNodes[1]);
        splitNodes[1].select(0, 0);
      } else {
        splitNodes[1];
        parentInsertNode.append(splitNodes[0]);
        splitNodes[0].select();
      }
    } else {
      splitNodes[1].remove();
      let selectedOffset = splitNodes[0].getTextContent().length;
      let newTextNode = $createTextNode(splitNodes[0].getTextContent() + splitNodes[2].getTextContent());
      parentInsertNode.append(newTextNode);
      newTextNode.select(selectedOffset, selectedOffset);
    }
  }
  // all other nodes that are not insert or delete must be wrapped within a delete node
  else {
    let delNode = $createDeleteNode(username);
    node.insertBefore(delNode);
    let newParaNode = $createParagraphNode();
    newParaNode.append(node);
    if (node.__type === 'image') {
      node.remove();
      return true;
    }
    let splitNodes = node.splitText(...[anchorOffset, focusOffset]);
    if (splitNodes.length == 1) {
      delNode.append(splitNodes[0]);
      delNode.select();
    } else if (splitNodes.length == 2) {
      if (selectedArea == 'left') {
        delNode.append(splitNodes[0]);
        delNode.insertAfter(splitNodes[1]);
        delNode.select();
      } else {
        delNode.append(splitNodes[1]);
        delNode.insertBefore(splitNodes[0]);
        splitNodes[0].select();
      }
    } else {
      delNode.append(splitNodes[1]);
      delNode.insertBefore(splitNodes[0]);
      delNode.insertAfter(splitNodes[2]);
      if (selectedArea == 'left') {
        splitNodes[0].select();
      } else {
        delNode.select();
      }
    }
  }
  // if there is data it must be inserted at the selected focus offset
  if (data) {
    handleInsert(data, username);
  }
}

/**
 * Handles the selection and insertion or deletion of data.
 *
 * @param {string | null} data - The data to be inserted.
 */
export function handleSelection(data: string | null, username: string) {
  let selection = $getSelection(); // get the current selection
  if ($isRangeSelection(selection)) {
    // get all the nodes that are selected along with the focus and anchor details
    let selectedNodes = selection.getNodes();
    let anchorOffset = selection.anchor.offset;
    let focusOffset = selection.focus.offset;
    let anchorKey = selection.anchor.key;
    let focusKey = selection.focus.key;

    // when a user tries to select within a single node
    if (selectedNodes.length == 1) {
      let node = selectedNodes[0];
      handleSingleNodeSplit(node, anchorOffset, focusOffset, data, username);
    } else {
      // setting the selected area, when the users selects from bottom to top or vice-versa the selection area differs
      let anchor = false;
      for (let node of selectedNodes) {
        if (node.__key == anchorKey) {
          anchor = true;
          break;
        }

        if (node.__key == focusKey) {
          break;
        }
      }

      let anchorSelectedArea = 'left';
      let focusSelectedArea = 'right';

      if (anchor) {
        anchorSelectedArea = 'right';
        focusSelectedArea = 'left';
      }

      // for selection based insertion or deletion the node where the anchor node or focus node points to must be split and wrapped accordingly all other nodes can be wrapped within the delete node provide it is not already a delete node
      for (let node of selectedNodes) {
        if ((node.__type === 'text' || node.__type === 'phx-text') && node.__key === anchorKey) {
          //splitting anchor node
          handleMultiNodeSplit(node, anchorOffset, anchorSelectedArea, username);
        } else if ((node.__type === 'text' || node.__type === 'phx-text') && node.__key === focusKey) {
          //splitting focus node and inserting the data if its not null
          let split_node = handleMultiNodeSplit(node, focusOffset, focusSelectedArea, username);
          if (Array.isArray(split_node)) {
            $setSelection(split_node[0].select(0, 0));
          } else {
            $setSelection(split_node.select());
          }

          if (data) {
            handleInsert(data, username);
          }
        } else if (node.__type === 'text' || node.__type === 'phx-text' || node.__type === 'phx-paragraph') {
          // all other nodes that were fully selected
          handleMultiNodeSplit(node, -1, anchorSelectedArea, username);
        }
      }
    }

    if ($isRangeSelection(selection)) {
      mergeDelete(selection, username);
    }
  }

  return true;
}

/**
 * Handles the insertion of data.
 *
 * @param {string} data - The data to be inserted.
 */
export function handleInsert(
  data: string,
  username: string,
  parentNode?: ElementNode,
  nextSiblingNode?: LexicalNode | null
) {
  let selection = $getSelection(); // Get the current selection
  if ($isRangeSelection(selection)) {
    let insertNode = $createInsertNode(username);
    insertNode.append($createTextNode(data));

    if (parentNode) {
      // Insert before the original node's next sibling (or at end if null)
      if (nextSiblingNode) {
        nextSiblingNode.insertBefore(insertNode);
      } else {
        parentNode.append(insertNode);
      }

      // Set the cursor to the end of the inserted node
      const lastChild = insertNode.getLastChild();
      if (lastChild && $isTextNode(lastChild)) {
        selection.anchor.set(lastChild.getKey(), lastChild.getTextContentSize(), 'text');
        selection.focus.set(lastChild.getKey(), lastChild.getTextContentSize(), 'text');
        $setSelection(selection);
      }
    } else {
      let currentNode: LexicalNode | any = selection.getNodes()[0];
      // get the node that is selected along with the anchor and focus details
      let anchorKey = selection.anchor.key;
      let focusKey = selection.focus.key;
      let anchorOffset = selection.anchor.offset;
      let focusOffset = selection.focus.offset;
      // if the anchor and focus offset are different it generally means that a bunch of content was selected
      if (anchorOffset !== focusOffset) {
        return handleSelection(data, username);
      }
      // edge case when the anchor and focus offset are same but the keys are different, it still means that a bunch of paragraphs were selected.
      if (anchorOffset == focusOffset && anchorKey !== focusKey) {
        return handleSelection(data, username);
      }
      if (currentNode.__type == 'root') {
        const root = $getRoot();
        const paragraph = $createParagraphNode();
        const newInsert = $createInsertNode(username);
        const textNode = $createTextNode('');
        newInsert.append(textNode);
        paragraph.append(newInsert);
        root.append(paragraph);

        // Place caret inside the new text node and insert the character(s)
        textNode.select();
        selection.insertText(data);
        return true;
      }
      // inserting data within a insert node
      if (currentNode.getParent().__type == 'insert') {
        let insertNode = currentNode.getParent();
        if (insertNode.__username == username) {
          selection.insertText(data);
          insertNode.setNewDate();
        } else {
          let newInsertNode = $createInsertNode(username);
          insertNode.insertAfter(newInsertNode);
          let splitNodes = currentNode.splitText(anchorOffset);
          insertNode.append(splitNodes[0]);
          if (splitNodes.length !== 1) {
            let oldInsertNode = $createInsertNode(insertNode.__username);
            newInsertNode.insertAfter(oldInsertNode);
            oldInsertNode.append(splitNodes[1]);
          }
          let textNode = $createTextNode('');
          newInsertNode.append(textNode);
          textNode.select().insertText(data);
        }
      }
      // inserting data within a delete node
      else if (currentNode.getParent().__type == 'delete') {
        let insertNode = $createInsertNode(username);
        insertNode.append($createTextNode(data));
        currentNode.getParent().insertAfter(insertNode);
        $setSelection(insertNode.select());
      }
      // inserting data within any other node
      else {
        let insertNode = $createInsertNode(username);
        selection.insertNodes([insertNode]);
        selection.insertText(data);
      }
    }
  }
  return true;
}

function checkSiblingsForNonDelete(previousSibling: LexicalNode | any): LexicalNode | boolean {
  let currentNode = previousSibling?.getLastChild();

  while (currentNode) {
    if (!$isDeleteNode(currentNode)) {
      return currentNode;
    }
    currentNode = currentNode.getPreviousSibling();
  }

  return false;
}

/**
 * Handles the deletion of data.
 */
function handleDelete(username: string) {
  let selection = $getSelection(); // get the current selection
  if ($isRangeSelection(selection)) {
    // get the anchor and focus details
    let anchorOffset = selection.anchor.offset;
    let focusOffset = selection.focus.offset;
    let anchorKey = selection.anchor.key;
    let focusKey = selection.focus.key;
    // if the anchor and focus offset are different it generally means that a bunch of content was selected

    if (anchorOffset !== focusOffset) {
      return handleSelection(null, username);
    }

    // edge case when the anchor and focus offset are same but the keys are different, it still means that a bunch of paragraphs were selected.
    if (anchorOffset == focusOffset && anchorKey !== focusKey) {
      return handleSelection(null, username);
    }

    let currentNode: LexicalNode | any = selection.getNodes()[0];

    if (!currentNode) {
      return true;
    }

    if (currentNode.__type === 'image') {
      currentNode.remove();
      return true;
    }

    if (currentNode.__type === 'phx-paragraph' && currentNode.getTextContent().trim() === '') {
      let previousSibling = currentNode.getPreviousSibling();
      currentNode.remove(); // Remove empty paragraph

      // Move selection to previous valid node
      if (previousSibling) {
        let lastDescendant = previousSibling.getLastDescendant();
        if (lastDescendant) {
          lastDescendant.select();
        } else {
          previousSibling.select();
        }
      }
      return true;
    }

    // Handle deletion of empty list items
    if (currentNode.__type === 'listitem') {
      const listItemNode = currentNode as PhxListItemNode;
      const listItemContent = listItemNode.getTextContent().trim();

      // Check if the list item is empty or only contains whitespace
      if (listItemContent === '' || anchorOffset === 0) {
        const parentList = listItemNode.getParent();
        const previousSibling = listItemNode.getPreviousSibling();
        const nextSibling = listItemNode.getNextSibling();

        // Remove the empty list item
        listItemNode.remove();

        // If this was the last item in the list, remove the entire list
        if ($isListNode(parentList) && parentList.getChildrenSize() === 0) {
          const listPreviousSibling = parentList.getPreviousSibling();
          parentList.remove();

          // Move selection to previous valid node
          if (listPreviousSibling) {
            const lastDescendant = findLastDescendant(listPreviousSibling);
            if (lastDescendant) {
              (lastDescendant as any).select();
            } else if ((listPreviousSibling as any).select) {
              (listPreviousSibling as any).select();
            }
          }
        } else {
          // Move selection to the previous or next list item
          if (previousSibling) {
            const lastDescendant = findLastDescendant(previousSibling);
            if (lastDescendant) {
              (lastDescendant as any).select();
            } else if ((previousSibling as any).select) {
              (previousSibling as any).select();
            }
          } else if (nextSibling) {
            const firstDescendant = findFirstDescendant(nextSibling);
            if (firstDescendant) {
              (firstDescendant as any).select();
            } else if ((nextSibling as any).select) {
              (nextSibling as any).select();
            }
          }
        }

        return true;
      }
    }

    // If cursor is at the start of a non-empty paragraph, allow normal deletion behavior
    const parentNode = currentNode.getParent();
    const isAtStart = anchorOffset === 0;
    const isDeleteNodeAtStart = !parentNode.getPreviousSibling() && parentNode.__type === 'delete' && isAtStart;

    if (isDeleteNodeAtStart) {
      return false;
    }

    if (
      isAtStart &&
      currentNode.__type === 'phx-text' &&
      parentNode.__type !== 'insert' &&
      parentNode.__type !== 'phx-paragraph' &&
      parentNode.__type !== 'paragraph' &&
      !parentNode.getPreviousSibling()
    ) {
      if (parentNode.__type === 'delete') {
        const previousSibling = parentNode.getParent().getPreviousSibling();
        if (!previousSibling.getFirstChild()) previousSibling.remove();
        else {
          const lastChild = previousSibling.getLastChild();
          if (lastChild) {
            lastChild.insertAfter(parentNode);
          }
          const nextSibiling = parentNode.getParent().getNextSibling();
          if (!nextSibiling.getTextContent()) nextSibiling.remove();
        }
        return true;
      }

      let previousSiblingLastChild = parentNode.getPreviousSibling()?.getLastChild();

      while (previousSiblingLastChild && previousSiblingLastChild.getType() === 'delete') {
        const nonDeleteNode = checkSiblingsForNonDelete(previousSiblingLastChild.getParent());
        if (nonDeleteNode) {
          previousSiblingLastChild = nonDeleteNode;
        } else {
          previousSiblingLastChild = previousSiblingLastChild
            .getParent()
            .getPreviousSibling()
            .getLastChild() as TextNode;
        }
      }

      if (previousSiblingLastChild) {
        if (previousSiblingLastChild.getType() === 'insert') {
          const previousParent = previousSiblingLastChild.getParent();
          const firstChild = previousSiblingLastChild.getFirstChild();

          currentNode.remove();
          previousParent.append(currentNode);

          const newCursorPosition = firstChild.getTextContent().length;
          selection.setTextNodeRange(firstChild, newCursorPosition, firstChild, newCursorPosition);
        } else {
          const currentText = currentNode.getTextContent();
          const previousText = previousSiblingLastChild.getTextContent();
          const mergedText = previousText + currentText;

          previousSiblingLastChild.setTextContent(mergedText);
          currentNode.remove();

          // Set the selection to the end of previousSiblingText
          const newCursorPosition = previousText.length;
          selection.setTextNodeRange(
            previousSiblingLastChild,
            newCursorPosition,
            previousSiblingLastChild,
            newCursorPosition
          );
        }
      } else {
        const prevSib = parentNode.getPreviousSibling();
        if (prevSib) {
          prevSib.remove();
        }
      }
      return true; // Prevent getting stuck
    } else if (anchorOffset === 0 && !parentNode.getPreviousSibling() && parentNode.__type === 'insert') {
      const previousParentSibling = parentNode.getParent().getPreviousSibling();
      if (previousParentSibling) {
        const lastChild = previousParentSibling.getLastChild();
        if (lastChild && lastChild.__type === 'insert') {
          // Merge text of currentNode with lastChild
          const lastChildLength = lastChild.getTextContent().length;
          lastChild.getLastChild().setTextContent(lastChild.getTextContent() + currentNode.getTextContent());
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.setTextNodeRange(
              lastChild.getLastChild(),
              lastChildLength,
              lastChild.getLastChild(),
              lastChildLength
            );
          }
          currentNode.remove();
        } else if (lastChild) {
          lastChild.insertAfter(parentNode);
        } else {
          previousParentSibling.remove();
        }
      }
      return true;
    }

    // When cursor is at position 0, check for previous sibling within same parent
    if (anchorOffset === 0) {
      const parentType = currentNode.getParent()?.__type;
      // If we're in a phx-paragraph, paragraph, or listitem, check for previous sibling node
      if (parentType === 'phx-paragraph' || parentType === 'paragraph' || parentType === 'listitem') {
        let prevSibling = currentNode.getPreviousSibling();

        if (prevSibling) {
          // If previous sibling is a delete node, find the node BEFORE the delete node
          if (prevSibling.__type === 'delete') {
            let nodeBeforeDelete = prevSibling.getPreviousSibling();
            // Keep going back until we find a non-delete node
            while (nodeBeforeDelete && nodeBeforeDelete.__type === 'delete') {
              nodeBeforeDelete = nodeBeforeDelete.getPreviousSibling();
            }

            if (nodeBeforeDelete) {
              // Move cursor to end of the node before the delete
              const lastDesc = findLastDescendant(nodeBeforeDelete);
              if (lastDesc && $isTextNode(lastDesc)) {
                const len = lastDesc.getTextContent().length;
                lastDesc.select(len, len);
              } else if ((nodeBeforeDelete as any).select) {
                (nodeBeforeDelete as any).select();
              }
              return true;
            } else {
              // No node before delete - we're at start of paragraph
              // Move to previous paragraph
              const paraNode = currentNode.getParent();
              const prevPara = paraNode?.getPreviousSibling();
              if (prevPara) {
                const lastDesc = findLastDescendant(prevPara);
                if (lastDesc && $isTextNode(lastDesc)) {
                  const len = lastDesc.getTextContent().length;
                  lastDesc.select(len, len);
                } else if ((prevPara as any).select) {
                  (prevPara as any).select();
                }
              }
              return true;
            }
          } else {
            // Previous sibling is not a delete node, move cursor there
            const lastDesc = findLastDescendant(prevSibling);
            if (lastDesc && $isTextNode(lastDesc)) {
              const len = lastDesc.getTextContent().length;
              lastDesc.select(len, len);
            } else if ((prevSibling as any).select) {
              (prevSibling as any).select();
            }
            return true;
          }
        }

        // No previous sibling in same paragraph - we're at start of paragraph
        // Move to previous paragraph if exists
        const paraNode = currentNode.getParent();
        const prevPara = paraNode?.getPreviousSibling();
        if (prevPara) {
          const lastDesc = findLastDescendant(prevPara);
          if (lastDesc && $isTextNode(lastDesc)) {
            const len = lastDesc.getTextContent().length;
            lastDesc.select(len, len);
          } else if ((prevPara as any).select) {
            (prevPara as any).select();
          }
        }
        return true;
      }

      // For other parent types (not paragraph), traverse up
      if (parentType !== 'paragraph' && parentType !== 'phx-paragraph') {
        let node = currentNode;
        while (node && node.__type !== 'paragraph' && node.__type !== 'phx-paragraph') {
          let prevSibling = node.getPreviousSibling();
          if (prevSibling) {
            const lastDescendant = findLastDescendant(prevSibling);
            if (lastDescendant && $isTextNode(lastDescendant)) {
              const len = lastDescendant.getTextContent().length;
              lastDescendant.select(len, len);
            } else if ((prevSibling as any).select) {
              (prevSibling as any).select();
            }
            return true;
          } else {
            node = node.getParent();
          }
        }

        if (node) {
          let previousSibling = node.getPreviousSibling();
          if (previousSibling) {
            let descendant = (previousSibling as any).getLastDescendant?.();
            if (descendant) {
              descendant.select();
            } else if ((previousSibling as any).select) {
              (previousSibling as any).select();
            }
          }
        }
        return true;
      }
    }

    // logic for deleting a insert node
    if (currentNode.getParent().__type == 'insert') {
      let parentInsertNode = currentNode.getParent();
      // when the user tries to delete the content from the end or a single character content
      if (anchorOffset == currentNode?.getTextContent().length) {
        let paraNode = $createParagraphNode();
        paraNode.append(currentNode);
        let splitNodes = currentNode.splitText(...[anchorOffset - 1]);
        if (splitNodes.length == 1) {
          let prevSibling = parentInsertNode.getPreviousSibling();
          if (prevSibling && prevSibling.select) {
            parentInsertNode.remove();
            prevSibling.select();
          } else {
            const nextSibling = parentInsertNode.getNextSibling();
            let parentParaNode = parentInsertNode.getParent();
            parentInsertNode.remove();
            if (nextSibling) {
              nextSibling.select(0, 0);
            } else {
              parentParaNode.select();
            }
          }
        } else {
          parentInsertNode.append(splitNodes[0]);
          splitNodes[1].remove();
          splitNodes[0].select();
        }
      }
      // when the user tries to delete a content within the middle
      else {
        const textContent = currentNode?.getTextContent();
        if (anchorOffset === 0) {
          // When cursor is at the start, only remove the first character
          const newText = textContent.slice(1);
          const newTextNode = $createTextNode(newText);
          parentInsertNode.append(newTextNode);
          currentNode.remove();
          newTextNode.select(0, 0);
        } else {
          // For cursor positions within the text
          const beforeText = textContent.slice(0, anchorOffset - 1);
          const afterText = textContent.slice(anchorOffset);
          const newTextNode = $createTextNode(beforeText + afterText);
          parentInsertNode.append(newTextNode);
          currentNode.remove();
          newTextNode.select(anchorOffset - 1, anchorOffset - 1);
        }
      }
      // merging all the delete nodes to avoid duplicates
      mergeDelete(selection, username);
      return true;
    }

    // when the user tries to delete a delete node
    if (currentNode.getParent().__type == 'delete') {
      let parentDeleteNode = currentNode.getParent();
      let prevSibling = parentDeleteNode.getPreviousSibling();
      if (prevSibling && prevSibling.select) {
        prevSibling.select();
        return true;
      }
      currentNode.getParent().select(0, 0);
      mergeDelete(selection, username);
      return true;
    }

    // when to user tries to delete any other node
    // Removing content from the end of a string or deleting a single character from the content.
    if (anchorOffset == currentNode?.getTextContent().length) {
      let deleteNode = $createDeleteNode(username);
      currentNode.insertAfter(deleteNode);
      let paraNode = $createParagraphNode();
      paraNode.append(currentNode);
      let splitNodes = currentNode.splitText(...[anchorOffset - 1]);
      if (splitNodes.length == 1) {
        deleteNode.append(splitNodes[0]);
        splitNodes[0].select(0, 0);
      } else {
        deleteNode.insertBefore(splitNodes[0]);
        deleteNode.append(splitNodes[1]);
        splitNodes[0].select();
      }
    }
    // Removing the content from the middle
    else {
      let deleteNode = $createDeleteNode(username);
      currentNode.insertAfter(deleteNode);
      let paraNode = $createParagraphNode();
      paraNode.append(currentNode);
      if (currentNode.type === 'image') {
        currentNode.remove();
        return true;
      }
      let splitNodes = currentNode.splitText(...[anchorOffset - 1, anchorOffset]);
      if (splitNodes.length == 2) {
        deleteNode.append(splitNodes[0]);
        deleteNode.insertAfter(splitNodes[1]);
        splitNodes[0].select(0, 0);
      } else {
        deleteNode.append(splitNodes[1]);
        deleteNode.insertBefore(splitNodes[0]);
        deleteNode.insertAfter(splitNodes[2]);
        splitNodes[0].select();
      }
    }
    // merging duplicate delete nodes
    mergeDelete(selection, username);
  }
  return true;
}

/**
 * Handles forward deletion (Delete key) - deletes character to the right of cursor
 */
function handleForwardDelete(username: string): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return true;
  }

  const anchorOffset = selection.anchor.offset;
  const focusOffset = selection.focus.offset;
  const anchorKey = selection.anchor.key;
  const focusKey = selection.focus.key;

  // If there's already a selection range, handle it the same way as backspace
  if (anchorOffset !== focusOffset || anchorKey !== focusKey) {
    return handleSelection(null, username);
  }

  const currentNode: LexicalNode | any = selection.getNodes()[0];
  if (!currentNode) {
    return true;
  }

  const textContent = currentNode?.getTextContent() || '';
  const parentNode = currentNode.getParent();
  const parentType = parentNode?.__type;

  // Handle delete node - just move cursor to start of next non-delete node (don't delete)
  if (parentType === 'delete') {
    let nextSibling = parentNode.getNextSibling();
    while (nextSibling && nextSibling.__type === 'delete') {
      nextSibling = nextSibling.getNextSibling();
    }
    if (nextSibling) {
      const firstDesc = findFirstDescendant(nextSibling);
      if (firstDesc && $isTextNode(firstDesc)) {
        firstDesc.select(0, 0);
      }
    }
    // Don't recursively call - just move cursor, next keypress will delete
    return true;
  }

  // If cursor is at end of text, need to go to next node within same paragraph
  if (anchorOffset >= textContent.length) {
    // First, try to find next sibling within the same parent (e.g., within phx-paragraph)
    let nextSibling = currentNode.getNextSibling();

    // Skip delete nodes
    while (nextSibling && nextSibling.__type === 'delete') {
      nextSibling = nextSibling.getNextSibling();
    }

    if (nextSibling) {
      // Found next node in same paragraph - just move cursor there
      const firstDesc = findFirstDescendant(nextSibling);
      if (firstDesc && $isTextNode(firstDesc)) {
        firstDesc.select(0, 0);
        // Don't recursively call - next keypress will delete
      }
    }
    // At end of paragraph or moved cursor - don't do anything else
    return true;
  }

  // Handle insert node - directly modify text
  if (parentType === 'insert') {
    const newText = textContent.slice(0, anchorOffset) + textContent.slice(anchorOffset + 1);
    if (newText.length === 0) {
      const nextSibling = parentNode.getNextSibling();
      const prevSibling = parentNode.getPreviousSibling();
      parentNode.remove();
      if (nextSibling) {
        const firstDesc = findFirstDescendant(nextSibling);
        if (firstDesc && $isTextNode(firstDesc)) {
          firstDesc.select(0, 0);
        }
      } else if (prevSibling) {
        const lastDesc = findLastDescendant(prevSibling);
        if (lastDesc && $isTextNode(lastDesc)) {
          lastDesc.select();
        }
      }
    } else {
      currentNode.setTextContent(newText);
      currentNode.select(anchorOffset, anchorOffset);
    }
    return true;
  }

  // Regular text (phx-text or text) - wrap the character in a delete node
  const charToDelete = textContent.charAt(anchorOffset);
  if (!charToDelete) return true;

  // Create delete node with the character (use $createTextNode as that's what other parts use)
  const deleteNode = $createDeleteNode(username);
  deleteNode.append($createTextNode(charToDelete));

  // Modify the current node's text
  const beforeText = textContent.slice(0, anchorOffset);
  const afterText = textContent.slice(anchorOffset + 1);

  if (beforeText.length === 0 && afterText.length === 0) {
    // Single character - replace node with delete node
    currentNode.insertBefore(deleteNode);
    currentNode.remove();
    // Find next node for cursor
    let nextSibling = deleteNode.getNextSibling();
    while (nextSibling && nextSibling.__type === 'delete') {
      nextSibling = nextSibling.getNextSibling();
    }
    if (nextSibling) {
      const firstDesc = findFirstDescendant(nextSibling);
      if (firstDesc && $isTextNode(firstDesc)) {
        firstDesc.select(0, 0);
      }
    }
  } else if (beforeText.length === 0) {
    // Deleting first character
    currentNode.insertBefore(deleteNode);
    currentNode.setTextContent(afterText);
    currentNode.select(0, 0);
  } else if (afterText.length === 0) {
    // Deleting last character
    currentNode.setTextContent(beforeText);
    currentNode.insertAfter(deleteNode);
    // Find next node for cursor after the delete node
    let nextSibling = deleteNode.getNextSibling();
    while (nextSibling && nextSibling.__type === 'delete') {
      nextSibling = nextSibling.getNextSibling();
    }
    if (nextSibling) {
      const firstDesc = findFirstDescendant(nextSibling);
      if (firstDesc && $isTextNode(firstDesc)) {
        firstDesc.select(0, 0);
      }
    }
  } else {
    // Deleting from middle - need to split the node properly
    // beforeText stays in current node, deleteNode in middle, afterText in new node
    currentNode.setTextContent(beforeText);
    currentNode.insertAfter(deleteNode);
    
    // Create a new text node for the afterText
    const afterTextNode = $createTextNode(afterText);
    deleteNode.insertAfter(afterTextNode);
    
    // Position cursor at start of afterText node
    afterTextNode.select(0, 0);
  }

  return true;
}

function findLastDescendant(node: LexicalNode | null | undefined): LexicalNode | null {
  if ($isElementNode(node)) {
    const children = node.getChildren();
    if (children.length > 0) {
      return findLastDescendant(children[children.length - 1]);
    }
  }
  return $isTextNode(node) ? node : null;
}

function findFirstDescendant(node: LexicalNode | null | undefined): LexicalNode | null {
  if ($isElementNode(node)) {
    const children = node.getChildren();
    if (children.length > 0) {
      return findFirstDescendant(children[0]);
    }
  }
  return $isTextNode(node) ? node : null;
}

async function handleSelectionKeyDown(event: KeyboardEvent, username: string) {
  const selection = $getSelection();

  if ($isRangeSelection(selection)) {
    const anchorOffset = selection.anchor.offset;
    const focusOffset = selection.focus.offset;
    const anchorKey = selection.anchor.key;
    const focusKey = selection.focus.key;

    if (anchorOffset !== focusOffset || anchorKey !== focusKey) {
      handleDelete(username);
    }

    let currentNode: LexicalNode | any = selection.getNodes()[0];

    // Safer type checking approach
    const isInListItem = (node: any): boolean => {
      if (!node || typeof node.getParent !== 'function') {
        return false;
      }

      const parent = node.getParent();
      if (!parent) return false;

      // Check if parent is a list item
      if (parent.__type === 'listitem') {
        return true;
      }

      // Check if grandparent is a list item
      if (typeof parent.getParent === 'function') {
        const grandParent = parent.getParent();
        if (grandParent && grandParent.__type === 'listitem') {
          return true;
        }
      }

      return false;
    };

    if (isInListItem(currentNode)) {
      // Let default list behavior handle it
      return false;
    }

    if (event.key === 'Enter' && anchorOffset === focusOffset && currentNode.getParent().__type === 'insert') {
      event.preventDefault();
      const textContent = currentNode.getTextContent();
      const cursorPosition = selection.anchor.offset;
      const beforeCursorText = textContent.slice(0, cursorPosition);
      const afterCursorText = textContent.slice(cursorPosition);
      currentNode.setTextContent(beforeCursorText);
      const newParagraphNode = $createParagraphNode();
      const parentNode = currentNode.getParent().getParent() as ElementNode;
      parentNode.insertAfter(newParagraphNode);

      handleInsert(afterCursorText, username, newParagraphNode, null);

      selection.anchor.set(newParagraphNode.getKey(), 0, 'element');
      selection.focus.set(newParagraphNode.getKey(), 0, 'element');
      $setSelection(selection);

      let sibling = currentNode.getParent().getNextSibling();
      while (sibling) {
        const nextSibling = sibling.getNextSibling();
        sibling.remove();
        newParagraphNode.append(sibling);
        sibling = nextSibling;
      }
      return true;
    }
  }

  if (event.key === 'Backspace') {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const nodes = selection.getNodes();
      if (nodes.length > 0) {
        const node = nodes[0];

        // Check if we're in a list item
        if (node && typeof node.getParent === 'function') {
          const parent = node.getParent();
          if (parent) {
            const isParentListItem = parent.__type === 'listitem';
            const hasGrandParent = typeof parent.getParent === 'function';

            let isGrandParentListItem = false;
            if (hasGrandParent) {
              const grandParent = parent.getParent();
              if (grandParent) {
                isGrandParentListItem = grandParent.__type === 'listitem';
              }
            }

            if (isParentListItem || isGrandParentListItem) {
              // Let default list behavior handle it
              return false;
            }
          }
        }
      }
    }

    event.preventDefault();
    const nodes = selection?.getNodes();
    nodes?.forEach((node) => {
      if ($isTextNode(node)) {
        handleDelete(username);
      } else if ($isPhxParagraphNode(node)) {
        handleDelete(username);
      } else if (node instanceof PhxInsertNode) {
        node.remove();
      } else if (node instanceof PhxDeleteNode) {
        return;
      }
    });
  }
  return true;
}

export default function PhxTrackChangesPlugin({ username }: { username: string }) {
  const [editor] = useLexicalComposerContext();
  const { trackChangesState: isEnabled } = useTrackChanges();

  // Track only new text nodes when track changes is enabled
  useEffect(() => {
    return editor.registerMutationListener(TextNode, (mutations) => {
      editor.update(() => {
        for (const [key, type] of mutations.entries()) {
          if (type === 'created') {
            const node = $getNodeByKey(key);
            if (!node || !$isTextNode(node)) continue;

            const parent = node.getParent();
            if (!parent) continue;

            // Skip if already in an insert or delete node
            if (parent.__type === 'insert' || parent.__type === 'delete') {
              continue;
            }

            // Create insert node and wrap the text node
            const insertNode = $createInsertNode(username);
            node.insertBefore(insertNode);
            insertNode.append(node);
          }
        }
      });
    });
  }, [isEnabled, editor, username]);

  useEffect(() => {
    // Helper function to check if node is in a list item
    const isInListItem = (node: any): boolean => {
      if (!node || typeof node.getParent !== 'function') {
        return false;
      }

      const parent = node.getParent();
      if (!parent) return false;

      // Check if parent is a list item
      if (parent.__type === 'listitem') {
        return true;
      }

      // Check if grandparent is a list item
      if (typeof parent.getParent === 'function') {
        const grandParent = parent.getParent();
        if (grandParent && grandParent.__type === 'listitem') {
          return true;
        }
      }

      return false;
    };

    return mergeRegister(
      // KEY_DOWN_COMMAND handler
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          if ((event.metaKey || event.ctrlKey) && (event.key === 'a' || event.key === 'A')) return false;
          if ((event.metaKey || event.ctrlKey) && (event.key === 'z' || event.key === 'Z')) return false; // Prevent undo
          if ((event.metaKey || event.ctrlKey) && (event.key === 'c' || event.key === 'C')) return true; // Allow copy
          if ((event.metaKey || event.ctrlKey) && (event.key === 'v' || event.key === 'V')) {
            return true;
          }
          if ((event.metaKey || event.ctrlKey) && (event.key === 'x' || event.key === 'X')) {
            event.preventDefault();
            editor.dispatchCommand(COPY_COMMAND, event);

            // Only handle delete with track changes if enabled
            setTimeout(() => {
              editor.update(() => {
                handleDelete(username);
              });
            }, 0);
          }

          // Check if we're in a list item before handling the Enter key
          if (event.key === 'Enter') {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              const nodes = selection.getNodes();
              if (nodes.length > 0) {
                const node = nodes[0];

                // Check if we're in a list item
                if (node && typeof node.getParent === 'function') {
                  const parent = node.getParent();
                  // First check if parent exists before checking properties
                  if (parent) {
                    const isParentListItem = parent.__type === 'listitem';
                    const hasGrandParent = typeof parent.getParent === 'function';

                    // Check for grandparent only if the parent has a getParent function
                    let isGrandParentListItem = false;
                    if (hasGrandParent) {
                      const grandParent = parent.getParent();
                      if (grandParent) {
                        isGrandParentListItem = grandParent.__type === 'listitem';
                      }
                    }

                    if (isParentListItem || isGrandParentListItem) {
                      // Let default list behavior handle it
                      return false;
                    }
                  }
                }
              }
            }
            // Defer handling to KEY_ENTER_COMMAND
            return false;
          }

          if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
            event.preventDefault();
            return handleInsert(event.key, username);
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),

      // KEY_ENTER_COMMAND handler
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          const result = handleSelectionKeyDown(event as unknown as KeyboardEvent, username);
          return typeof result === 'boolean' ? result : true;
        },
        COMMAND_PRIORITY_HIGH
      ),

      // KEY_BACKSPACE_COMMAND handler - Override backspace keypress operation
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        (event) => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const nodes = selection.getNodes();
            const node = nodes[0];

            if (isInListItem(node)) {
              // Let default list behavior handle it
              return false;
            }

            if (!selection.isCollapsed()) {
              handleSelectionKeyDown(event, username);
              return true;
            } else {
              event.preventDefault();
              return handleDelete(username);
            }
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),

      // KEY_DELETE_COMMAND handler - Override delete keypress operation (fn+delete on Mac)
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        (event) => {
          event.preventDefault();
          return handleForwardDelete(username);
        },
        COMMAND_PRIORITY_HIGH
      ),

      // DELETE_CHARACTER_COMMAND handler - Override ctrl+backspace operation
      editor.registerCommand(
        DELETE_CHARACTER_COMMAND,
        (isBackward: boolean) => {
          if (isBackward) {
            return handleDelete(username);
          } else {
            return handleForwardDelete(username);
          }
        },
        COMMAND_PRIORITY_HIGH
      ),

      // DELETE_WORD_COMMAND handler - Override shift+delete operation
      editor.registerCommand(
        DELETE_WORD_COMMAND,
        (isBackward: boolean) => {
          if (isBackward) {
            return handleDelete(username);
          } else {
            return handleForwardDelete(username);
          }
        },
        COMMAND_PRIORITY_HIGH
      ),

      // DELETE_LINE_COMMAND handler - Override ctrl+delete operation
      editor.registerCommand(
        DELETE_LINE_COMMAND,
        (_isBackward: boolean) => {
          return handleDelete(username);
        },
        COMMAND_PRIORITY_HIGH
      ),

      // COPY_COMMAND handler - Register explicit command handlers for copy
      editor.registerCommand(
        COPY_COMMAND,
        () => {
          // Always allow copy to proceed normally
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),

      // CUT_COMMAND handler - Register explicit command handlers for cut
      editor.registerCommand(
        CUT_COMMAND,
        (_event) => {
          // First copy, then handle the delete with track changes
          setTimeout(() => {
            editor.update(() => {
              handleDelete(username);
            });
          }, 0);

          return true;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, username, isEnabled]);

  return null;
}
